import React, { useEffect } from 'react';

import {
  SR_API_ACTION_SET_REDIRECTION,
  SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
  SR_API_ACTION_DELETE_REDIRECTION,
  SR_API_ACTION_DRIVER_UPDATE_LISTING,
  SR_API_ACTION_SUNLOCK_GET_BY_SERIAL_NUMBER,
  SR_API_ACTION_CODEBOX_GET_BY_SERIAL_NUMBER,
  SR_API_ACTION_CODEBOX_ASSIGN_LISTING_ID,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_SUNSETUP_DRIVER_ORDERS,
} from 'common/dataTableConstants';

import {
  LISTING_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS,
} from 'common/listingConstants';

import useBranch from 'client/hooks/useBranch';
import JobActions from 'client/actions/Job';
import InventoryActions from 'client/actions/Inventory';
import SunlockActions from 'client/actions/Sunlock';
import postWithState from 'client/actions/postWithState';

import SrFlex from 'client/components/SrFlex';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrSunSetupDriverJobPage from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobPage';

function getDataTableSettingsFromProps({
  driverId,
  orderId,
}) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_SUNSETUP_DRIVER_ORDERS,
    rootViewId: DATA_TABLE_SOURCE_SUNSETUP_DRIVER_ORDERS,
    initialViewSettings: {
      filter: {
        'jobOrders.id': orderId,
        'jobOrders.driverId': driverId,
      },
      sort: {
        'jobOrders.createdDate': 'desc',
      },
    },
    count: 1,
    itemsPropName:'jobOrders',
  };

  return dataTableSettings;
}

const SrSunSetupDriverJob = ({
  driverId,
  orderId,
  jobId,
  history,
}) => {
  const {
    dispatch,
    userAgent,
    appUrl,
  } = useBranch({
    userAgent: ['analytics', 'userAgent'],
    appUrl: ['env', 'APP_URL'],
  });

  let _reloadJobOrder;

  useEffect(() => {
    const interval = global.setInterval(() => {
      if (_reloadJobOrder) {
        _reloadJobOrder();
      }
    }, 2000);
    return () => {
      global.clearInterval(interval);
    };
  });

  return (
    <SrDataTableConnected
      {...getDataTableSettingsFromProps({ driverId, orderId })}
    >
      {({ jobOrders, dataTableApiState, reloadItems, onInfiniteScrollWrapperRef }) => {
        const jobOrder = jobOrders[0];
        const job = jobOrder && jobOrder.jobs.find((job) => job.id === jobId);

        const reloadJobOrder = () => {
          reloadItems({
            count: 1,
            filter: {
              'jobOrders.id': orderId,
            },
          });
        };

        _reloadJobOrder = reloadJobOrder;

        const onUpdateJob = ({ updates }) => {
          return dispatch(JobActions.update, {
            jobId,
            updates,
          }).then(reloadJobOrder);
        };

        const onUpdateListing = ({ updates }) => {
          return dispatch(postWithState, {
            action: SR_API_ACTION_DRIVER_UPDATE_LISTING,
            payload: {
              listingId: jobOrder.listingId,
              updates,
            },
          }).then(reloadJobOrder);
        };

        const onSunlockBarcodeText = ({ barcode, raspberryPiSerialNumber }) => {
          let _sunlock = null;
          return Promise.resolve()
            .then(() => {
              if (barcode && !raspberryPiSerialNumber) {
                return dispatch(InventoryActions.getByBarcode, { barcode });
              }
              return { raspberryPiSerialNumber };
            })
            .then((item) => {
              if (item && item.raspberryPiSerialNumber) {
                return dispatch(postWithState, {
                  action: SR_API_ACTION_SUNLOCK_GET_BY_SERIAL_NUMBER,
                  payload: {
                    serialNumber: item.raspberryPiSerialNumber,
                  },
                });
              }
            })
            .then((sunlock) => {
              if (sunlock) {
                _sunlock = sunlock;
                return dispatch(SunlockActions.assign, {
                  listingId: jobOrder.listing.id,
                  sunlockId: sunlock.id,
                  triggeredBy: 'Driver Dashboard',
                });
              }
            })
            .then(reloadJobOrder)
            .then(() => _sunlock);
        };

        const onCodeboxBarcodeText = ({ barcode, codeboxSerialNumber }) => {
          let _codeboxLock = null;
          return Promise.resolve()
            .then(() => {
              if (barcode && !codeboxSerialNumber) {
                return dispatch(InventoryActions.getByBarcode, { barcode });
              }
              return { codeboxSerialNumber };
            })
            .then((item) => {
              if (item && item.codeboxSerialNumber) {
                return dispatch(postWithState, {
                  action: SR_API_ACTION_CODEBOX_GET_BY_SERIAL_NUMBER,
                  payload: {
                    serialNumber: item.codeboxSerialNumber,
                  },
                });
              }
            })
            .then((codeboxLock) => {
              if (codeboxLock) {
                _codeboxLock = codeboxLock;
                return dispatch(postWithState, {
                  action: SR_API_ACTION_CODEBOX_ASSIGN_LISTING_ID,
                  payload: {
                    codeboxLockId: codeboxLock.id,
                    triggeredBy: 'Driver Dashboard',
                    listingId: jobOrder.listing.id,
                  },
                });
              }
            })
            .then(reloadJobOrder)
            .then(() => _codeboxLock);
        };

        const onYardSignBarcodeText = ({ barcode, yardSignUrl }) => {
          let _yardSignUrl = null;
          return Promise.resolve()
            .then(() => {
              if (!yardSignUrl && barcode) {
                return dispatch(InventoryActions.getByBarcode, { barcode });
              }
              return { yardSignUrl };
            })
            .then((item) => {
              if (item && item.yardSignUrl) {
                _yardSignUrl = item.yardSignUrl;
                return dispatch(postWithState, {
                  action: SR_API_ACTION_GET_REDIRECTION_BY_LISTING_ID,
                  payload: {
                    listingId: jobOrder.listing.id,
                  },
                });
              }
            })
            .then((listingRedirection) => {
              if (listingRedirection) {
                return dispatch(postWithState, {
                  action: SR_API_ACTION_DELETE_REDIRECTION,
                  payload: {
                    id: listingRedirection.id,
                  },
                });
              }
            })
            .then(() => {
              if (_yardSignUrl) {
                const redirectionSourceUrl = _yardSignUrl.split('/').pop().trim().toUpperCase();
                return dispatch(postWithState, {
                  action: SR_API_ACTION_SET_REDIRECTION,
                  payload: {
                    id: redirectionSourceUrl,
                    url: (
                      `/listing/${jobOrder.listingId}?${LISTING_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS}`
                    ),
                  },
                });
              }
            })
            .then(reloadJobOrder)
            .then(() => _yardSignUrl);
        };

        return (
          <>
            <div ref={onInfiniteScrollWrapperRef}>
              {job && (
                <SrSunSetupDriverJobPage
                  userAgent={userAgent}
                  job={job}
                  jobOrderId={orderId}
                  driverId={driverId}
                  history={history}
                  listing={jobOrder.listing}
                  sunlock={jobOrder.sunlock}
                  codeboxLock={jobOrder.codeboxLock}
                  yardSignUrl={jobOrder.yardSignUrl}
                  onUpdateJob={onUpdateJob}
                  onUpdateListing={onUpdateListing}
                  onSunlockBarcodeText={onSunlockBarcodeText}
                  onYardSignBarcodeText={onYardSignBarcodeText}
                  onCodeboxBarcodeText={onCodeboxBarcodeText}
                  appUrl={appUrl}
                />
              )}
              {dataTableApiState.inProgress && (
                <SrFlex justify='center'>
                  <div className='loading-gif' />
                </SrFlex>
              )}
            </div>
          </>
        );
      }}
    </SrDataTableConnected>
  );
};

SrSunSetupDriverJob.displayName = 'SrSunSetupDriverJob';

export default SrSunSetupDriverJob;
